import React, { useState, useEffect } from 'react';
import { useLaserEyes } from '@omnisat/lasereyes';
import { verifySignatureAndOrdinals } from '../../services/authService';
import { useLanguage } from '../../i18n/LanguageContext';

// Definir a interface Window para incluir as propriedades das carteiras
declare global {
  interface Window {
    unisat?: any;
    xverse?: any;
    xverseWallet?: any;
    bitcoin?: {
      xverse?: any;
      [key: string]: any;
    };
    phantom?: any;
    okx?: any;
    wizz?: any;
    oyl?: any;
    [key: string]: any; // Para permitir propriedades dinâmicas
  }
}

interface WalletConnectButtonProps {
  style?: React.CSSProperties;
  onAccessChange?: (hasAccess: boolean) => void;
}

/**
 * Componente simplificado de conexão de carteira seguindo exatamente a documentação do LaserEyes
 */
const WalletConnectButton: React.FC<WalletConnectButtonProps> = ({ style, onAccessChange }) => {
  // Usar o contexto de idioma
  const { t } = useLanguage();

  // Estados para controlar o fluxo de autenticação
  const [showWallets, setShowWallets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0); // 0: inicial, 1: conectado, 2: autenticado
  const [hasAccess, setHasAccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Usar o hook do LaserEyes exatamente como na documentação
  const { connect, disconnect, connected, address, signMessage } = useLaserEyes();

  // Log para debug apenas em ambiente de desenvolvimento
  if (process.env.NODE_ENV === 'development') {
    console.log('WalletConnectButton - Estado atual:', {
      connected,
      address,
      currentStep,
      hasAccess,
      isLoading,
      error
    });
  }

  // Lista de carteiras suportadas - apenas UniSat, Xverse e OYL conforme solicitado
  const wallets = [
    { name: 'UniSat', icon: '🔶', type: 'unisat' },
    { name: 'Xverse', icon: '🔷', type: 'xverse' },
    { name: 'OYL', icon: '🟠', type: 'oyl' },
  ];

  // Função para conectar carteira
  const handleConnect = async (walletType: string) => {
    try {
      // Sempre logar em produção e desenvolvimento para diagnóstico
      console.log(`[WalletConnectButton] Tentando conectar carteira do tipo: ${walletType}`);

      // Verificar se a carteira está disponível no objeto window
      if (typeof window !== 'undefined') {
        let walletAvailable = false;

        if (walletType === 'unisat') {
          walletAvailable = !!window.unisat;
        } else if (walletType === 'xverse') {
          // Verificação mais detalhada para Xverse
          walletAvailable = !!window.xverse ||
                           (!!window.bitcoin && !!window.bitcoin.xverse) ||
                           !!window.xverseWallet ||
                           Object.keys(window).some(key =>
                             key.toLowerCase().includes('xverse') ||
                             (typeof window[key] === 'object' && window[key] !== null &&
                              Object.keys(window[key]).some(subKey => subKey.toLowerCase().includes('xverse')))
                           );
        } else if (walletType === 'oyl') {
          walletAvailable = !!window.oyl;
        }

        console.log(`[WalletConnectButton] Carteira ${walletType} disponível:`, walletAvailable);

        // Para Xverse, vamos tentar conectar mesmo se não detectarmos explicitamente
        // porque o LaserEyes SDK pode ter métodos internos para detectá-la
        if (!walletAvailable && walletType !== 'xverse') {
          console.error(`[WalletConnectButton] Carteira ${walletType} não detectada no navegador`);
          setError(`Carteira ${walletType} não detectada. Por favor, instale a extensão ${walletType} e recarregue a página.`);
          return;
        } else if (!walletAvailable && walletType === 'xverse') {
          console.warn(`[WalletConnectButton] Carteira Xverse não detectada explicitamente, mas tentaremos conectar mesmo assim`);
        }
      }

      setError(null);
      setIsLoading(true);
      setStatusMessage(t('connecting'));

      // Passo 1: Conectar carteira
      console.log(`[WalletConnectButton] Chamando connect('${walletType}')`);
      await connect(walletType);

      // Sempre logar em produção e desenvolvimento para diagnóstico
      console.log(`[WalletConnectButton] Conexão com ${walletType} bem-sucedida!`);

      setShowWallets(false);
      setCurrentStep(1);
      setStatusMessage(null);
    } catch (err) {
      console.error(`[WalletConnectButton] Erro ao conectar ${walletType}:`, err);

      // Mensagem de erro mais detalhada
      let errorMessage = 'Erro ao conectar carteira';

      if (err instanceof Error) {
        errorMessage = `Erro: ${err.message}`;
        console.error('[WalletConnectButton] Detalhes do erro:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
      }

      // Mensagens de erro específicas para problemas comuns
      if (errorMessage.includes('not installed') || errorMessage.includes('not found')) {
        errorMessage = `Carteira ${walletType} não instalada ou não detectada. Por favor, instale a extensão e recarregue a página.`;
      } else if (errorMessage.includes('user rejected') || errorMessage.includes('canceled')) {
        errorMessage = `Conexão rejeitada pelo usuário. Por favor, tente novamente e aprove a conexão na extensão da carteira.`;
      }

      setError(errorMessage);
      setStatusMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para autenticar (passos 2-6)
  const handleAuthenticate = async () => {
    if (!address) return;

    setIsLoading(true);
    setError(null);

    try {
      // Passo 2: Gerar mensagem única
      const nonce = Math.random().toString(36).substring(2, 15);
      const timestamp = new Date().toISOString();
      const message = `multiverso.club wants you to sign in with your Bitcoin account to prove you have Multiverso Pass: ${address}\n\nNonce: ${nonce}\nIssued At: ${timestamp}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('Mensagem gerada:', message);
      }

      // Passo 3: Assinar mensagem
      setStatusMessage(t('requestingSignature'));
      if (process.env.NODE_ENV === 'development') {
        console.log('Solicitando assinatura da mensagem');
      }

      let signature;
      try {
        signature = await signMessage(message);
      } catch (signError) {
        console.error('Erro ao assinar com método simples, tentando alternativa:', signError);
        signature = await signMessage({ message, address });
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Assinatura obtida:', signature);
      }

      // Passos 4 e 5: Verificar assinatura e validar Ordinals
      setStatusMessage(t('verifyingSignature'));
      if (process.env.NODE_ENV === 'development') {
        console.log('Enviando para backend verificar assinatura e validar Ordinals');
      }

      const authResult = await verifySignatureAndOrdinals(address, message, signature);
      if (process.env.NODE_ENV === 'development') {
        console.log('Resultado da autenticação:', authResult);
      }

      // Passo 6: Liberar acesso
      setCurrentStep(2);
      setHasAccess(authResult.hasAccess);
      setStatusMessage(null);

      // Notificar o componente pai sobre o status de acesso
      onAccessChange?.(authResult.hasAccess);

      if (!authResult.success) {
        // Falha na verificação (APIs indisponíveis)
        setError(authResult.message || "Falha na verificação de Ordinals. Serviços de API indisponíveis.");
      }
    } catch (error) {
      console.error('Erro durante a autenticação:', error);
      setStatusMessage(null);

      if (error instanceof Error) {
        setError(`Falha na autenticação: ${error.message}`);
      } else {
        setError('Falha na autenticação. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resetar o status de acesso quando desconectar
  useEffect(() => {
    if (!connected && hasAccess) {
      setHasAccess(false);
      onAccessChange?.(false);
    }
  }, [connected, hasAccess, onAccessChange]);

  // Usuário não conectado - mostrar opções de carteira
  if (!connected) {
    return (
      <div className="wallet-connect-container" style={style}>
        <button
          className="wallet-connect-button"
          onClick={() => setShowWallets(!showWallets)}
          disabled={isLoading}
        >
          <span className="wallet-icon">₿</span>
          <span>{isLoading ? t('connecting') : t('connectWallet')}</span>
        </button>

        {showWallets && (
          <div className="wallet-options">
            {wallets.map((wallet) => (
              <button
                key={wallet.name}
                className="wallet-option-button"
                onClick={() => handleConnect(wallet.type)}
                disabled={isLoading}
              >
                <span>{wallet.icon}</span> {wallet.name}
              </button>
            ))}
          </div>
        )}

        {statusMessage && <div className="status-message">{statusMessage}</div>}
        {error && <div className="wallet-error">{error}</div>}

        <style jsx>{`
          .wallet-connect-container {
            position: relative;
          }
          .wallet-connect-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            border-radius: 8px;
            background-color: #f7931a;
            color: white;
            font-weight: 500;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .wallet-icon {
            font-size: 18px;
          }
          .wallet-options {
            position: absolute;
            top: 100%;
            right: 0;
            width: 200px;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            padding: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            z-index: 1000;
            margin-top: 4px;
          }
          .wallet-option-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 6px;
            background-color: #f5f5f5;
            color: #333;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
            text-align: left;
          }
          .wallet-option-button:hover:not(:disabled) {
            background-color: #e0e0e0;
          }
          .status-message {
            margin-top: 8px;
            padding: 6px 10px;
            background-color: rgba(33, 150, 243, 0.1);
            color: #2196F3;
            border-radius: 4px;
            font-size: 12px;
            text-align: center;
          }
          .wallet-error {
            margin-top: 8px;
            padding: 6px 10px;
            background-color: rgba(244, 67, 54, 0.1);
            color: #f44336;
            border-radius: 4px;
            font-size: 12px;
            text-align: center;
          }
          button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    );
  }

  // Usuário conectado mas não autenticado
  if (currentStep === 1) {
    return (
      <div className="wallet-connect-container" style={style}>
        <div className="wallet-connected">
          <div className="wallet-address">
            {address?.substring(0, 6)}...{address?.substring(address.length - 4)}
          </div>



          <div className="wallet-actions">
            <button
              className="wallet-authenticate-button"
              onClick={handleAuthenticate}
              disabled={isLoading}
            >
              {isLoading ? t('authenticating') : t('authenticate')}
            </button>

            <button
              className="wallet-disconnect-button"
              onClick={disconnect}
              disabled={isLoading}
            >
              {t('disconnectWallet')}
            </button>
          </div>
        </div>

        {statusMessage && <div className="status-message">{statusMessage}</div>}
        {error && <div className="wallet-error">{error}</div>}

        <style jsx>{`
          .wallet-connect-container {
            position: relative;
          }
          .wallet-connected {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .wallet-address {
            font-family: monospace;
            padding: 6px 10px;
            background-color: rgba(247, 147, 26, 0.1);
            border-radius: 4px;
            font-size: 14px;
          }
          .step-indicator {
            font-size: 12px;
            color: #4CAF50;
            font-weight: 500;
          }
          .wallet-actions {
            display: flex;
            gap: 8px;
          }
          .wallet-authenticate-button,
          .wallet-disconnect-button {
            padding: 8px 12px;
            border-radius: 6px;
            font-weight: 500;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .wallet-authenticate-button {
            background-color: #4CAF50;
            color: white;
          }
          .wallet-disconnect-button {
            background-color: #f44336;
            color: white;
          }
          .status-message {
            margin-top: 8px;
            padding: 6px 10px;
            background-color: rgba(33, 150, 243, 0.1);
            color: #2196F3;
            border-radius: 4px;
            font-size: 12px;
            text-align: center;
          }
          .wallet-error {
            margin-top: 8px;
            padding: 6px 10px;
            background-color: rgba(244, 67, 54, 0.1);
            color: #f44336;
            border-radius: 4px;
            font-size: 12px;
            text-align: center;
          }
          button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    );
  }

  // Usuário autenticado
  return (
    <div className="wallet-connect-container" style={style}>
      <div className="wallet-authenticated">
        <div className="wallet-address">
          {address?.substring(0, 6)}...{address?.substring(address.length - 4)}
        </div>

        <div className="access-status">
          {hasAccess ? (
            <span className="access-granted">✓ {t('accessGranted')}</span>
          ) : (
            <span className="access-denied">{t('accessDenied')}</span>
          )}
        </div>

        <div className="wallet-buttons">
          <button
            className="wallet-disconnect-button"
            onClick={disconnect}
          >
            {t('disconnectWallet')}
          </button>


        </div>
      </div>

      {statusMessage && <div className="status-message">{statusMessage}</div>}
      {error && <div className="wallet-error">{error}</div>}

      <style jsx>{`
        .wallet-connect-container {
          position: relative;
        }
        .wallet-authenticated {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .wallet-address {
          font-family: monospace;
          padding: 6px 10px;
          background-color: rgba(247, 147, 26, 0.1);
          border-radius: 4px;
          font-size: 14px;
        }
        .step-indicator {
          font-size: 12px;
          color: ${hasAccess ? '#4CAF50' : '#f44336'};
          font-weight: 500;
        }
        .access-status {
          font-size: 14px;
          font-weight: 500;
        }
        .access-granted {
          color: #4CAF50;
        }
        .access-denied {
          color: #f44336;
        }
        .wallet-buttons {
          display: flex;
          gap: 8px;
        }
        .wallet-disconnect-button {
          padding: 8px 12px;
          border-radius: 6px;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .wallet-disconnect-button {
          background-color: #f44336;
          color: white;
        }

        .status-message {
          margin-top: 8px;
          padding: 6px 10px;
          background-color: rgba(33, 150, 243, 0.1);
          color: #2196F3;
          border-radius: 4px;
          font-size: 12px;
          text-align: center;
        }
        .wallet-error {
          margin-top: 8px;
          padding: 6px 10px;
          background-color: rgba(244, 67, 54, 0.1);
          color: #f44336;
          border-radius: 4px;
          font-size: 12px;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default WalletConnectButton;
